/**
 * NearbyPricesBlock — "Price at nearby stores" for a product.
 *
 * Renders on the FoodItemPage and FoodPulseProductDetail. Uses the
 * user's saved location (from LocationPicker → localStorage), or a
 * lightweight inline picker if nothing is set. Talks to
 * GET /api/products/{slug}/nearby-prices which is location-aware and
 * returns unit-price normalisation (per-kg + per-lb).
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Store, TrendingDown, Loader2, ArrowRight, ExternalLink, Sparkles } from "lucide-react";
import { http } from "@/lib/api";
import LocationPicker, { getStoredLocation } from "@/components/LocationPicker";

const GREEN = "#0F3D2E";
const AMBER = "#E4A140";
const RED   = "#7B2A2E";

const DEFAULT_RADIUS = 25;

export default function NearbyPricesBlock({ productSlug, productName }) {
  const [location, setLocation] = useState(getStoredLocation());
  const [prices, setPrices] = useState([]);
  const [best, setBest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [radius, setRadius] = useState(DEFAULT_RADIUS);
  const [showPicker, setShowPicker] = useState(!getStoredLocation());

  const load = useCallback(async () => {
    if (!productSlug) return;
    setLoading(true);
    try {
      const params = { radius_km: radius, limit: 12 };
      if (location?.latitude && location?.longitude) {
        params.lat = location.latitude; params.lon = location.longitude;
      }
      const [near, bp] = await Promise.all([
        http.get(`/products/${productSlug}/nearby-prices`, { params }),
        http.get(`/products/${productSlug}/best-price`),
      ]);
      setPrices(near.data?.items || []);
      setBest(bp.data?.best || null);
    } catch { setPrices([]); setBest(null); }
    setLoading(false);
  }, [productSlug, radius, location]);

  useEffect(() => { load(); }, [load]);

  return (
    <section className="border-t border-b" style={{ borderColor: "#E9D8B4", background: "#FFF7E4" }} data-testid="nearby-prices-block">
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color: AMBER }}>
              — Nearby prices & offers
            </div>
            <h3 className="font-display text-2xl" style={{ color: GREEN }}>
              Where to buy {productName || "this"} right now
            </h3>
          </div>
          <div className="min-w-[220px] max-w-[340px] flex-1">
            {location && !showPicker ? (
              <button onClick={() => setShowPicker(true)}
                      className="w-full flex items-center gap-2 rounded-full border-2 bg-white px-3 py-2 text-sm hover:bg-amber-50"
                      style={{ borderColor: "#E9D8B4", color: GREEN }}
                      data-testid="nearby-loc-open">
                <MapPin className="w-4 h-4" />
                <span className="font-semibold truncate">{location.city || location.formatted_address}</span>
                <span className="opacity-50 text-xs ml-auto">change</span>
              </button>
            ) : (
              <LocationPicker value={location} onChange={(loc) => { setLocation(loc); setShowPicker(false); }} compact />
            )}
          </div>
        </div>

        {/* Best-price banner */}
        {best && (
          <div className="rounded-2xl p-3 mb-4 flex items-center gap-3" style={{ background: "#FFEDD5", border: `1px solid ${AMBER}` }}>
            <Sparkles className="w-5 h-5 flex-shrink-0" style={{ color: RED }} />
            <div className="text-sm flex-1 min-w-0">
              <span className="font-semibold" style={{ color: GREEN }}>Best price anywhere right now: </span>
              <span className="font-bold" style={{ color: RED }}>${best.deal_price}</span>
              {best.unit && <span className="opacity-70"> / {best.unit}</span>}
              <span className="opacity-70"> at {best.vendor_name || best.city}</span>
            </div>
            <Link to={`/food-pulse/deals/${best.slug}`} className="text-xs font-semibold underline flex items-center gap-1 flex-shrink-0" style={{ color: GREEN }}
                  data-testid="nearby-best-link">
              See offer <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
        )}

        <div className="flex gap-1.5 mb-4">
          {[5, 10, 25, 50, 100].map((r) => (
            <button key={r} onClick={() => setRadius(r)}
                    className="rounded-full px-2.5 py-1 text-[11px] font-semibold border"
                    style={{
                      background:  radius === r ? GREEN : "transparent",
                      color:       radius === r ? "white" : GREEN,
                      borderColor: radius === r ? GREEN : "#E9D8B4",
                    }}
                    data-testid={`nearby-radius-${r}`}>
              {r} km
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin inline opacity-50" /></div>
        ) : prices.length === 0 ? (
          <div className="rounded-2xl bg-white p-6 text-center border" style={{ borderColor: "#E9D8B4" }}>
            <TrendingDown className="w-8 h-8 mx-auto opacity-40 mb-2" />
            <div className="text-sm" style={{ color: GREEN }}>
              No active deals for this item within {radius} km yet.{" "}
              <Link to="/food-pulse/spot" className="underline font-semibold">Spot one nearby?</Link>
            </div>
          </div>
        ) : (
          <div className="grid gap-2">
            {prices.map((p) => (
              <Link key={p.id || p.slug} to={`/food-pulse/deals/${p.slug}`}
                    className="rounded-xl bg-white border p-3 flex items-center gap-3 hover:shadow-md transition"
                    style={{ borderColor: "#E9D8B4" }}
                    data-testid={`nearby-price-${p.slug || p.id}`}>
                <div className="w-14 h-14 rounded-lg overflow-hidden bg-amber-50 flex-shrink-0">
                  {p.image_urls?.[0] && <img src={p.image_urls[0]} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate" style={{ color: GREEN }}>{p.vendor_name || p.city}</div>
                  <div className="text-xs opacity-70 flex items-center gap-2 flex-wrap">
                    {p.distance_km != null && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{p.distance_km} km</span>}
                    {p.city && <span>{p.city}</span>}
                    {p.price_per_kg != null && <span>${p.price_per_kg}/kg</span>}
                    {p.price_per_lb != null && <span>${p.price_per_lb}/lb</span>}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="font-display text-lg font-bold" style={{ color: RED }}>${p.deal_price ?? p.price}</div>
                  {p.original_price && p.original_price !== p.deal_price && (
                    <div className="text-[11px] line-through opacity-50">${p.original_price}</div>
                  )}
                </div>
                <ArrowRight className="w-4 h-4 opacity-40 flex-shrink-0" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
